import { Link, Typography, Stack, IconButton, Chip } from '@mui/material';
import {
  dateFormats,
  formatDateTime,
  fromUtcDateStringToDate,
  joinString,
  printRoundedNumber,
} from '../../shared/utils';
import { ErpLink } from './erp-link';
import { DataLink } from './data-link';
import { getComparator, sortTypes } from './sorting';
import { colors } from './ui-extension';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { shipmentStatuses } from './enums';
import { dataTransformers } from './data-transformers';

const statusColors = {
  pendingReview: 'warning',
  quote: 'warning',
  scheduled: 'primary',
  inTransit: 'secondary',
  delivered: 'success',
  completed: 'success',
  cancelled: 'error',
  // Add more status colors as needed
};

const commodityTypes = {
  case: 'case',
  pallet: 'pallet',
};

const getStatusColor = (status) => statusColors[status] || 'default';

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Reusable function to retrieve truckId and truckPosition from rowA and rowB
const retrieveTruckIdAndPositions = (rowA, rowB) => {
  const truckA = rowA?.[rowOriginalFields]?.truckIdAndPosition;
  const truckB = rowB?.[rowOriginalFields]?.truckIdAndPosition;

  const truckInfoA = truckA.split(' - ');
  const truckIdA = parseInt(truckInfoA[0], 10) || 0;
  const truckPositionA = parseInt(truckInfoA[1], 10) || 0;

  const truckInfoB = truckB.split(' - ');
  const truckIdB = parseInt(truckInfoB[0], 10) || 0;
  const truckPositionB = parseInt(truckInfoB[1], 10) || 0;

  return {
    truckIdA,
    truckIdB,
    truckPositionA,
    truckPositionB,
  };
};

const coreDataTypes = {
  number: (amount) => `${printRoundedNumber(amount, 0)}`,
  decimal: (amount) => `${printRoundedNumber(amount, 2)}`,
};

export const dataTypes = {
  date: dataTransformers.date,
  // Warning: Higher order function. --Zehao
  createCustomDate: dataTransformers.createCustomDate,
  boolean: (value) => (
    <Typography sx={{ color: !value ? colors.darkGray : undefined }}>
      {dataTransformers.boolean(value)}
    </Typography>
  ),
  currency: dataTransformers.currency,
  currencyHighAccuracy: dataTransformers.currencyHighAccuracy,
  unitPrice: dataTransformers.currencyHighAccuracy,
  quantity: dataTransformers.integer,
  number: dataTransformers.integer,
  string: (value) => `${value || ''}`,
  month: (month) => months[month - 1],
  decimal: coreDataTypes.decimal,
  monthYear: (monthYear) => {
    if (!monthYear) return '';
    const parsedDate = new Date(
      monthYear.year,
      monthYear.month,
      0,
      0,
      0,
      0
    );
    return `${months[parsedDate.getMonth()]} ${parsedDate.getFullYear()}`;
  },
  percentage: (percentage) => `${printRoundedNumber(percentage, 2)}%`,
  salesOrderNumber: (orderNo) =>
    orderNo && !isNaN(orderNo) ? (
      <Link href={`/order-entry?orderNo=${orderNo}`} target='_blank'>
        {orderNo}
      </Link>
    ) : (
      orderNo
    ),
  rmaNumber: (rmaNumber) => (
    <ErpLink
      windowName={'rmaentry'}
      query={{ order_no: rmaNumber }}
      text={rmaNumber}
    />
  ),
  itemId: (itemId) =>
    itemId === 'N/A' ? (
      itemId
    ) : (
      <DataLink link={`/item?itemId=${itemId}`} text={itemId} />
    ),
  invoiceNumber: (invoiceNo) => (
    <ErpLink
      windowName={'byinvoice'}
      query={{ invoice_no: invoiceNo }}
      text={invoiceNo}
    />
  ),
  opportunityNumber: (opportunityId) => (
    <ErpLink
      windowName={'opportunitymaintenance'}
      query={{ opportunity_id: opportunityId }}
      text={opportunityId}
    />
  ),
  camelCaseReplace: (key) =>
    key
      ?.replace(/([A-Z])/g, ' $1')
      ?.replace(/^./, (str) => str.toUpperCase())
      ?.replace('_', ' ') ||
    key ||
    '',

  // Shipping Queue Page Data Types
  shippingQueue: {
    shipmentReferenceId: (shipmentReferenceId) => {
      return `${shipmentReferenceId}`;
    },
    statusChip: (shipmentStatus) => {
      return (
        <Chip
          size='small'
          label={shipmentStatuses[shipmentStatus]}
          color={getStatusColor(shipmentStatus)}
        />
      );
    },
    link:
      ({ href, openNewTab, title }) =>
      (passedValue) => {
        return (
          <Link
            title={title}
            {...(openNewTab ? { target: '_blank' } : {})}
            href={href + passedValue}
          >
            {passedValue}
          </Link>
        );
      },
    truckInfo: () => (truckId, shipment) => {
      return (
        <>
          {truckId.includes('null') ? (
            <IconButton title='No assigned truck.' size='small'>
              <PriorityHighIcon color='error'></PriorityHighIcon>
            </IconButton>
          ) : (
            <IconButton title={`Truck ${truckId}`} size='small'>
              <LocalShippingIcon sx={{ color: shipment.color }} />
            </IconButton>
          )}
          <Typography color={!truckId.includes('null') ? 'primary' : 'error'}>
            {!truckId.includes('null') ? truckId : ''}
          </Typography>
        </>
      );
    },
    originAddress:
      (clickedAddress, moveToOnMap, trucks) => (originAddress, shipment) => {
        const {
          addressLine1: originAddressLine1,
          addressLine2: originAddressLine2,
          city: originCity,
          stateId: originStateId,
          postalCode: originPostalCode,
          countryId: originCountryId,
        } = originAddress;

        const originFullAddress = joinString(
          [
            originAddressLine1,
            originAddressLine2,
            originCity,
            originStateId,
            originPostalCode,
            originCountryId,
          ],
          ' '
        );

        const {
          addressLine1: destAddressLine1,
          addressLine2: destAddressLine2,
          city: destCity,
          stateId: destStateId,
          postalCode: destPostalCode,
          countryId: destCountryId,
        } = shipment.destinationAddress;

        const destFullAddress = joinString(
          [
            destAddressLine1,
            destAddressLine2,
            destCity,
            destStateId,
            destPostalCode,
            destCountryId,
          ],
          ' '
        );

        return (
          <Stack direction={'column'} spacing={1}>
            <Stack direction={'row'} spacing={1}>
              <Link
                color='#3700ff'
                title={`Origin Address: ${originFullAddress}`}
                onClick={() => {
                  const foundTruck = trucks.find(
                    (truck) => truck.truckId === +shipment?.truckId
                  );

                  clickedAddress({
                    address: originFullAddress,
                    addressType: 'origin',
                    truck: foundTruck || null,
                    openFlag: foundTruck ? true : false,
                  });
                }}
              >
                {originFullAddress}
              </Link>
              <Link
                color='#3700ff'
                title={`Navigate map to ${originFullAddress}`}
                onClick={() => {
                  const addressReqBody = {
                    address: originFullAddress,
                    position: { lat: '', lng: '' },
                  };
                  moveToOnMap(addressReqBody);
                }}
              >
                (Locate)
              </Link>
            </Stack>

            <Stack direction={'row'} spacing={1}>
              <Link
                color='#ff00c8'
                title={`Destination Address: ${originFullAddress}`}
                onClick={() => {
                  clickedAddress({
                    address: destFullAddress,
                    addressType: 'destination',
                    truck: shipment?.truckId
                      ? trucks.find(
                          (truck) => truck.truckId === +shipment?.truckId
                        )
                      : null,
                    openFlag: shipment?.truckId ? true : false,
                  });
                }}
              >
                {destFullAddress}
              </Link>
              <Link
                color='#ff00c8'
                title={`Navigate map to ${destFullAddress}`}
                onClick={() => {
                  const addressReqBody = {
                    address: destFullAddress,
                    position: { lat: '', lng: '' },
                  };

                  moveToOnMap(addressReqBody);
                }}
              >
                (Locate)
              </Link>
            </Stack>
          </Stack>
        );
      },
    commodityCount: (commodities) => {
      const pallets = commodities.reduce((num, commodity) => {
        if (commodity.commodityType === commodityTypes.pallet) {
          return num + +commodity.commodityCount;
        }
        return num;
      }, 0);
      return pallets;
    },
    commodityTotalWeight: (commodities) => {
      const weight = commodities.reduce((weight, commodity) => {
        if (commodity.commodityType === commodityTypes.pallet) {
          return weight + +commodity.commodityTotalWeight;
        }
        return weight;
      }, 0);
      return weight?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    shipmentStatus: (status) => {
      switch (status) {
        case 'scheduled':
          return <span style={{ color: 'green' }}>{status}</span>;
        case 'PendingReview':
          return <span style={{ color: 'orange' }}>{status}</span>;
        default:
          return <span style={{ color: 'black' }}>{status}</span>;
      }
    },
  },
  customerId: (customerId) => (
    <Link target='_blank' href={`/customer?customerId=${customerId}`}>
      {customerId}
    </Link>
  ),
};

export const getComparatorByType = ({
  columns = [],
  orderByCol = '',
  order = sortTypes.asc,
}) => {
  const column = columns.find((column) => column.id === orderByCol) || {};
  let { dataType, sortDataType, sortByField } = column;

  if (sortByField) {
    orderByCol = sortByField;
  }

  if (sortDataType) {
    dataType = sortDataType;
  }

  switch (dataType) {
    case dataTypes.currency:
    case dataTypes.currencyHighAccuracy:
    case dataTypes.unitPrice:
    case dataTypes.quantity:
      return (rowA, rowB) => {
        const numberA = +rowA?.[rowOriginalFields]?.[orderByCol];
        const numberB = +rowB?.[rowOriginalFields]?.[orderByCol];
        return order === sortTypes.asc ? numberA - numberB : numberB - numberA;
      };
    case dataTypes.date:
      return (rowA, rowB) => {
        const dateA = fromUtcDateStringToDate(
          rowA?.[rowOriginalFields]?.[orderByCol]
        );
        const dateB = fromUtcDateStringToDate(
          rowB?.[rowOriginalFields]?.[orderByCol]
        );
        return order === sortTypes.asc ? dateA - dateB : dateB - dateA;
      };
    case dataTypes.monthYear:
      return (rowA, rowB) => {
        return order === sortTypes.asc ? rowA - rowB : rowB - rowA;
      };
    case dataTypes.string:
    case dataTypes.rmaNumber:
    case dataTypes.salesOrderNumber:
    case dataTypes.opportunityNumber:
    case dataTypes.invoiceNumber:
    case dataTypes.itemId:
      return (rowA, rowB) => {
        const stringA = `${rowA?.[rowOriginalFields]?.[orderByCol] || ''}`
          .trim()
          .toLowerCase();
        const stringB = `${rowB?.[rowOriginalFields]?.[orderByCol] || ''}`
          .trim()
          .toLowerCase();

        if (!isNaN(stringA) && !isNaN(stringB)) {
          return order === sortTypes.asc
            ? stringA - stringB
            : stringB - stringA;
        }

        return order === sortTypes.asc
          ? stringA.localeCompare(stringB)
          : stringB.localeCompare(stringA);
      };
    // Shipping Queue Page Sorting
    case 'shippingQueue.addressSort':
      return (rowA, rowB) => {
        const addressA = rowA?.[rowOriginalFields]?.[orderByCol];
        const addressB = rowB?.[rowOriginalFields]?.[orderByCol];

        const fullAddressA = joinString(
          [
            addressA.addressLine1,
            addressA.addressLine2,
            addressA.city,
            addressA.stateId,
            addressA.postalCode,
            addressA.countryId,
          ],
          ' '
        );

        const fullAddressB = joinString(
          [
            addressB.addressLine1,
            addressB.addressLine2,
            addressB.city,
            addressB.stateId,
            addressB.postalCode,
            addressB.countryId,
          ],
          ' '
        );

        const { truckIdA, truckIdB, truckPositionA, truckPositionB } =
          retrieveTruckIdAndPositions(rowA, rowB);

        return order === sortTypes.asc
          ? fullAddressA.localeCompare(fullAddressB) ||
              truckIdA - truckIdB ||
              truckPositionA - truckPositionB
          : fullAddressB.localeCompare(fullAddressA) ||
              truckIdB - truckIdA ||
              truckPositionA - truckPositionB;
      };
    // Shipping Queue Page Sorting
    case 'shippingQueue.dateSort':
      return (rowA, rowB) => {
        const valueA = new Date(
          rowA?.[rowOriginalFields]?.[orderByCol]
        ).setHours(0, 0, 0, 0);
        const valueB = new Date(
          rowB?.[rowOriginalFields]?.[orderByCol]
        ).setHours(0, 0, 0, 0);

        const { truckIdA, truckIdB, truckPositionA, truckPositionB } =
          retrieveTruckIdAndPositions(rowA, rowB);

        // First compare by truckId, then always sort truckPosition in ascending order
        return order === sortTypes.asc
          ? valueA - valueB ||
              truckIdA - truckIdB ||
              truckPositionA - truckPositionB
          : valueB - valueA ||
              truckIdB - truckIdA ||
              truckPositionA - truckPositionB;
      };
    // Shipping Queue Page Sorting
    case 'shippingQueue.truckSort':
      return (rowA, rowB) => {
        const { truckIdA, truckIdB, truckPositionA, truckPositionB } =
          retrieveTruckIdAndPositions(rowA, rowB);

        // First compare by truckId, then always sort truckPosition in ascending order
        return order === sortTypes.asc
          ? truckIdA - truckIdB || truckPositionA - truckPositionB
          : truckIdB - truckIdA || truckPositionA - truckPositionB;
      };
    // Shipping Queue Page Sorting
    case 'shippingQueue.stringSort':
      return (rowA, rowB) => {
        const shipmentA = rowA?.[rowOriginalFields]?.[orderByCol];
        const shipmentB = rowB?.[rowOriginalFields]?.[orderByCol];

        const { truckIdA, truckIdB, truckPositionA, truckPositionB } =
          retrieveTruckIdAndPositions(rowA, rowB);

        return order === sortTypes.asc
          ? shipmentA.localeCompare(shipmentB) ||
              truckIdA - truckIdB ||
              truckPositionA - truckPositionB
          : shipmentB.localeCompare(shipmentA) ||
              truckIdB - truckIdA ||
              truckPositionA - truckPositionB;
      };
    // Shipping Queue Page Sorting
    case 'shippingQueue.numberSort':
      return (rowA, rowB) => {
        const shipmentA = rowA?.[rowOriginalFields]?.[orderByCol];
        const shipmentB = rowB?.[rowOriginalFields]?.[orderByCol];

        return order === sortTypes.asc
          ? shipmentA - shipmentB
          : shipmentB - shipmentA;
      };
    // Shipping Queue Page Sorting
    case 'shippingQueue.commodityWeightOrCount':
      return (rowA, rowB) => {
        //Grab nested array name and property to sort by nested in orderByCol split by '.'
        const parts = orderByCol.split('.');

        const commoditiesA = rowA?.[rowOriginalFields]?.[parts[0]];
        const commoditiesB = rowB?.[rowOriginalFields]?.[parts[0]];

        const countA = commoditiesA.reduce((num, commodity) => {
          return num + +commodity[parts[1]];
        }, 0);

        const countB = commoditiesB.reduce((num, commodity) => {
          return num + +commodity[parts[1]];
        }, 0);

        return order === sortTypes.asc ? countA - countB : countB - countA;
      };
    default:
      return getComparator(order, orderByCol);
  }
};

export const transformRowByColumns = (columns, row = {}, updateRow) => {
  const transformedRow = columns.reduce((transformedRow, column) => {
    const { id, dataType } = column;
    const value = row[id];
    transformedRow[id] =
      typeof dataType === 'function' ? dataType(value, row, updateRow) : value;

    transformedRow[rowOriginalFields] = { ...row };

    return transformedRow;
  }, {});

  if (row.autoExpand) {
    transformedRow.autoExpand = row.autoExpand;
  }

  if (row.expandableComponent) {
    transformedRow.expandableComponent = row.expandableComponent;
  }

  return transformedRow;
};

export const rowOriginalFields = '__rowOriginalFields';

export const transformRowsByColumns = (columns, rows = [], updateRow) =>
  rows.map((l) => transformRowByColumns(columns, l, updateRow));
